import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useAutoRefresh — polls a fetcher on an interval + on window focus /
 * visibility change so a stale customer page can't drift far from the
 * admin's ledger for long.
 *
 * Usage:
 *   const { data, lastUpdated, refresh, isRefreshing } = useAutoRefresh(
 *       () => api.get(url).then(r => r.data),
 *       { deps: [partyId], intervalMs: 30_000 }
 *   );
 */
export default function useAutoRefresh(fetcher, { deps = [], intervalMs = 30_000 } = {}) {
    const [data, setData] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const fetcherRef = useRef(fetcher);
    // Keep the latest fetcher in a ref so the interval doesn't restart on every render
    useEffect(() => { fetcherRef.current = fetcher; }, [fetcher]);

    const run = useCallback(async () => {
        setIsRefreshing(true);
        try {
            const result = await fetcherRef.current();
            setData(result);
            setLastUpdated(new Date());
        } catch {
            /* keep prior data — offline / transient error shouldn't blank the page */
        } finally {
            setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        run();
        const id = setInterval(run, intervalMs);
        const onFocus = () => run();
        const onVis = () => { if (document.visibilityState === "visible") run(); };
        // v1.25.54 — Global "refresh right now" bus. Anything on the app can
        // dispatch `window.dispatchEvent(new CustomEvent("micm-refresh"))` to
        // force every useAutoRefresh consumer to refetch. Used by the
        // fancy-thanks pipeline so the customer's ledger + dashboard tiles
        // update instantly the moment their payment is verified — no
        // manual reload, no 30-second wait for the next tick.
        const onRefresh = () => run();
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("micm-refresh", onRefresh);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("micm-refresh", onRefresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, intervalMs]);

    return { data, lastUpdated, refresh: run, isRefreshing };
}
